import { useState, useEffect } from 'react'
import ApiClient from '../../utils/ApiClient'

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const NOTE_MAX = 1000

export default function ConditionTiersSettings() {
    const [tiers, setTiers] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [saveMessage, setSaveMessage] = useState('')
    const [error, setError] = useState('')

    useEffect(() => {
        loadTiers()
    }, [])

    const loadTiers = async () => {
        try {
            setIsLoading(true)
            const response = await ApiClient.get('/api/adminsettings/condition-tiers')
            const data = Array.isArray(response.data) ? response.data : []
            // Order with New (code 11) first, then by code ascending
            const sorted = [...data].sort((a, b) => {
                if (a.code === 11) return -1
                if (b.code === 11) return 1
                return a.code - b.code
            })
            setTiers(sorted)
        } catch (err) {
            console.error('Failed to load condition tiers:', err)
            setError('Failed to load condition tiers')
        } finally {
            setIsLoading(false)
        }
    }

    const update = (code, field, value) => {
        setTiers(prev => prev.map(t => (t.code === code ? { ...t, [field]: value } : t)))
    }

    const saveTiers = async () => {
        try {
            setIsSaving(true)
            setError('')
            setSaveMessage('')
            await ApiClient.put('/api/adminsettings/condition-tiers', tiers)
            setSaveMessage('Saved')
            setTimeout(() => setSaveMessage(''), 3000)
        } catch (err) {
            console.error('Failed to save condition tiers:', err)
            setError(err?.response?.data?.error || 'Failed to save condition tiers. Please try again.')
        } finally {
            setIsSaving(false)
        }
    }

    if (isLoading) {
        return (
            <div className="py-6 sm:flex">
                <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Condition Tiers</dt>
                <dd className="mt-1 sm:mt-0 sm:flex-auto">
                    <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                </dd>
            </div>
        )
    }

    return (
        <div className="py-6 sm:flex">
            <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Condition Tiers</dt>
            <dd className="mt-1 sm:mt-0 sm:flex-auto">
                <div className="space-y-6">
                    <p className="text-sm text-gray-500">
                        MAP multiplier and customer-facing condition note per grade. A blank note means no
                        note is sent. New is read-only.
                    </p>

                    {error && (
                        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
                    )}

                    <div className="space-y-4">
                        {tiers.map(tier => {
                            const isNew = tier.code === 11
                            const multiplier = tier.map_multiplier
                            const note = tier.note || ''
                            return (
                                <div
                                    key={tier.code}
                                    className="border border-gray-200 rounded-lg p-4 shadow-sm space-y-3"
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-900">{tier.label}</span>
                                        {isNew && (
                                            <span className="text-xs text-gray-500 italic">Read-only</span>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            MAP Multiplier
                                            <span className="ml-2 text-gray-500">
                                                ({Math.round((multiplier || 0) * 100)}%)
                                            </span>
                                        </label>
                                        <input
                                            type="number"
                                            min="0"
                                            max="1"
                                            step="0.01"
                                            value={multiplier}
                                            disabled={isNew}
                                            onChange={(e) =>
                                                update(tier.code, 'map_multiplier', parseFloat(e.target.value))
                                            }
                                            className="block w-32 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500"
                                        />
                                    </div>

                                    {!isNew && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Condition Note
                                            </label>
                                            <textarea
                                                rows={3}
                                                maxLength={NOTE_MAX}
                                                value={note}
                                                onChange={(e) => update(tier.code, 'note', e.target.value)}
                                                placeholder="No note sent if left blank"
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                                            />
                                            <p className="mt-1 text-xs text-gray-500 text-right">
                                                {note.length}/{NOTE_MAX}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>

                    {/* Save Button and Status */}
                    <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={saveTiers}
                            disabled={isSaving}
                            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSaving ? 'Saving...' : 'Save Condition Tiers'}
                        </button>
                        {saveMessage && (
                            <span className="text-sm font-medium text-green-600">{saveMessage}</span>
                        )}
                    </div>
                </div>
            </dd>
        </div>
    )
}
